import { z } from "zod";

/**
 * Tarifregeln von HandVero (Stand: Entscheidung vom 17.10.2026).
 *
 * - Die ersten 100 verifizierten Firmen sind "Gründer": mindestens 3 Jahre kostenlos
 *   mit bis zu 3 Mitarbeitern.
 * - Jede weitere Firma nutzt HandVero kostenlos mit 1 Mitarbeiter.
 * - Mehr Mitarbeiter gibt es nur im Abo (Stufen Solo, Team, Betrieb, siehe SUBSCRIPTION_TIERS).
 *   Das Abo ist erst buchbar, wenn alles getestet ist.
 */

export const FOUNDER_SLOTS = 100;
export const FOUNDER_FREE_YEARS = 3;
export const FOUNDER_EMPLOYEE_LIMIT = 3;
export const BASIC_EMPLOYEE_LIMIT = 1;
/** Mitarbeiterlimit im Jahresabo, solange der Super-Admin nichts anderes festlegt. */
export const TEAM_DEFAULT_EMPLOYEE_LIMIT = 3;
/** Einstiegspreis des Abos („ab“): Team. */
export const SUBSCRIPTION_PRICE_EUR_PER_YEAR = 90;
export const SUBSCRIPTION_PRICE_EUR_PER_MONTH = 9;

export interface SubscriptionTier {
  id: "solo" | "team" | "business";
  name: string;
  /** Höchstzahl Mitarbeiter in dieser Stufe. */
  employeeLimit: number;
  monthlyCents: number;
  yearlyCents: number;
  /** Mitarbeiter, die im Grundpreis stecken; jeder weitere kostet `extraSeatMonthlyCents`. */
  includedSeats: number;
  extraSeatMonthlyCents: number;
}

/**
 * Abostufen, alle Preise netto pro Firma (Entscheidung von Tobias, 31.10.2026): Solo 1 Mitarbeiter 9 €, Team bis 3 für 19 €,
 * Betrieb ab 4 bis 10 für 19,99 € plus 2,99 € je Mitarbeiter ab dem vierten. Jahrespreis = 10 Monate (2 gratis).
 * Buchbar erst mit `SUBSCRIPTION_BOOKABLE`.
 */
export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  { id: "solo", name: "Solo", employeeLimit: 1, monthlyCents: 900, yearlyCents: 9000, includedSeats: 1, extraSeatMonthlyCents: 0 },
  { id: "team", name: "Team", employeeLimit: 3, monthlyCents: 1900, yearlyCents: 19000, includedSeats: 3, extraSeatMonthlyCents: 0 },
  { id: "business", name: "Betrieb", employeeLimit: 10, monthlyCents: 1999, yearlyCents: 19990, includedSeats: 3, extraSeatMonthlyCents: 299 },
];

export type SubscriptionInterval = "month" | "year";
export const SUBSCRIPTION_VAT_PERCENT = 19;

/** Preis einer Stufe für eine Mitarbeiterzahl und einen Zeitraum in Cent netto. Jahr = 10 Monate (2 gratis). */
export function tierPriceCents(tier: SubscriptionTier, employees: number, interval: SubscriptionInterval): number {
  const month = tier.monthlyCents + Math.max(0, employees - tier.includedSeats) * tier.extraSeatMonthlyCents;
  return interval === "month" ? month : month * 10;
}

/** Monatspreis einer Stufe bei einer bestimmten Mitarbeiterzahl in Cent. */
export function tierMonthlyCents(tier: SubscriptionTier, employees: number): number {
  return tier.monthlyCents + Math.max(0, employees - tier.includedSeats) * tier.extraSeatMonthlyCents;
}

/**
 * Das Jahresabo darf erst gebucht werden, wenn alles funktioniert und getestet ist.
 * Solange dieser Schalter `false` ist, gibt es keinen Weg, ein Abo zu buchen.
 */
export const SUBSCRIPTION_BOOKABLE = true as const;

export type PlanId = "founder" | "basic" | "team";

export interface PlanInput {
  founderNumber: number | null;
  founderUntil: Date | string | null;
  subscriptionUntil: Date | string | null;
  /** Vom Super-Admin gesetztes Limit für das Jahresabo. */
  seatLimitOverride?: number | null;
}

export interface PlanInfo {
  plan: PlanId;
  label: string;
  employeeLimit: number;
  /** Windows-Software: für alle Firmen, auch mit der kostenlosen Standardlizenz (Tobias, 2026-10-30). */
  desktopAccess: boolean;
  /** Ende des kostenlosen Gründerzeitraums oder des Abos. */
  validUntil: string | null;
}

function toDate(v: Date | string | null): Date | null {
  if (v === null) return null;
  return v instanceof Date ? v : new Date(v);
}

export function resolvePlan(input: PlanInput, now: Date = new Date()): PlanInfo {
  const subscriptionUntil = toDate(input.subscriptionUntil);
  if (subscriptionUntil && subscriptionUntil > now) {
    return {
      plan: "team",
      label: "Jahresabo",
      employeeLimit: input.seatLimitOverride ?? TEAM_DEFAULT_EMPLOYEE_LIMIT,
      desktopAccess: true,
      validUntil: subscriptionUntil.toISOString(),
    };
  }
  const founderUntil = toDate(input.founderUntil);
  if (input.founderNumber !== null && founderUntil && founderUntil > now) {
    return {
      plan: "founder",
      label: "Startaktion",
      employeeLimit: FOUNDER_EMPLOYEE_LIMIT,
      desktopAccess: true,
      validUntil: founderUntil.toISOString(),
    };
  }
  return { plan: "basic", label: "Kostenlos", employeeLimit: BASIC_EMPLOYEE_LIMIT, desktopAccess: true, validUntil: null };
}

export function founderUntilFrom(verifiedAt: Date): Date {
  const d = new Date(verifiedAt);
  d.setUTCFullYear(d.getUTCFullYear() + FOUNDER_FREE_YEARS);
  return d;
}

/**
 * Zusatzmodule (Stand: Preisvorschlag vom 26.10.2026, von Tobias übernommen). Alle Preise netto in Cent.
 * Noch nicht buchbar: freigeschaltet wird nur im Admin. Gebucht wird erst, wenn `SUBSCRIPTION_BOOKABLE` wahr ist.
 */
export interface AddonInfo {
  id: "billing" | "bank" | "plans";
  name: string;
  text: string;
  monthlyCents: number;
  yearlyCents: number;
  /** Braucht dieses andere Modul. */
  requires?: AddonInfo["id"];
}

export const ADDON_CATALOG: AddonInfo[] = [
  {
    id: "billing",
    name: "Rechnungen und Belege",
    text: "Angebote, Aufträge, Rechnungen und Mahnungen, Ausgaben und Belege, E-Rechnung, Übernahme aus anderen Programmen, Export für die Steuerberatung.",
    monthlyCents: 1200,
    yearlyCents: 12000,
  },
  {
    id: "bank",
    name: "Bankanbindung",
    text: "Das Geschäftskonto live verbinden: Zahlungen werden automatisch den offenen Rechnungen zugeordnet.",
    monthlyCents: 600,
    yearlyCents: 6000,
    requires: "billing",
  },
  {
    id: "plans",
    name: "Zeichenprogramm",
    text: "Installationspläne zeichnen, mit Wandansicht, Leitungswegen und Symbolen.",
    monthlyCents: 1200,
    yearlyCents: 12000,
  },
];

/** Weitere Mitarbeiter über die enthaltenen Plätze hinaus, pro Person und Monat in Cent. */
export const EXTRA_SEAT_MONTHLY_CENTS = 299;

export const formatEuro = (cents: number) =>
  `${Math.floor(cents / 100)}${cents % 100 ? `,${String(cents % 100).padStart(2, "0")}` : ""} €`;

/** Darf die Stufe diese Mitarbeiterzahl? Solo genau 1, Team 1 bis 3, Betrieb 4 bis 10. */
export function tierAllows(tier: SubscriptionTier, employees: number): boolean {
  const min = tier.id === "business" ? 4 : 1;
  return Number.isInteger(employees) && employees >= min && employees <= tier.employeeLimit;
}

export const subscriptionCheckoutSchema = z.object({
  tier: z.enum(["solo", "team", "business"]),
  interval: z.enum(["month", "year"]),
  employees: z.number().int().min(1).max(10),
});

export interface SubscriptionPaymentInfo {
  id: string;
  date: string;
  grossCents: number;
  status: "open" | "paid" | "failed" | "canceled" | "expired";
  periodStart: string | null;
  periodEnd: string | null;
  invoiceNumber: string | null;
}

export interface SubscriptionInfo {
  bookable: boolean;
  /** Ist die Bezahlung auf diesem Server eingerichtet (Mollie-Schlüssel hinterlegt)? */
  configured: boolean;
  current: { tier: SubscriptionTier["id"]; interval: SubscriptionInterval; employees: number; status: "pending" | "active" | "canceled" | "ended"; validUntil: string | null } | null;
  payments: SubscriptionPaymentInfo[];
}

/*
 * HandVero Zuhause (Privatpersonen). Die Anmeldung bleibt gesperrt, bis Tobias sie freigibt (wie SUBSCRIPTION_BOOKABLE);
 * die Vorschau kann sie zum Testen öffnen. Preise für Privatpersonen sind Endpreise inklusive Umsatzsteuer.
 */
export const PRIVATE_SIGNUP_OPEN = false as const;
/** Zuhause Plus (1,99 € im Monat) ist erst buchbar, wenn Zahlung und Rechtstexte fertig sind. */
export const PRIVATE_PLUS_BOOKABLE = false as const;
export const PRIVATE_PLUS_PRICE_CENTS = 199;
export const PRIVATE_FREE_STORAGE_MB = 1024;
export const PRIVATE_PLUS_STORAGE_MB = 10 * 1024;
